"use client";

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Users, Database, Mail, Download, Clock, Zap, TrendingUp, Building, Globe, AlertTriangle } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-white relative overflow-hidden">
      {/* Minimal Faint Line Patterns */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg width=%2240%22 height=%2240%22 viewBox=%220 0 40 40%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cpath d=%22M0 20 L40 20 M20 0 L20 40%22 stroke=%22%23f8fafc%22 stroke-width=%220.5%22/%3E%3C/svg%3E')]"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-gray-100/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container max-w-4xl mx-auto px-4 py-12 relative z-10">
        <Button variant="ghost" asChild className="mb-8">
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
        </Button>

        <Card>
          <CardHeader className="flex items-center space-x-2">
            <Shield className="h-5 w-5" />
            <CardTitle>Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 prose max-w-none">
            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Shield className="h-5 w-5 text-blue-500" />
                <span>1. Introduction</span>
              </h2>
              <p>At WightWay Connect, we are committed to protecting your privacy and ensuring that your personal data is handled in a safe and transparent manner. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website [wightwayconnect.uk], use our services, or interact with us in other ways.</p>
              <p>This Privacy Policy applies to information we collect through our Service and does not apply to information collected by third-party websites or services.</p>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Users className="h-5 w-5 text-green-500" />
                <span>2. Information We Collect</span>
              </h2>
              <p>We may collect the following types of information:</p>
              <h3 className="flex items-center space-x-2 mb-2">
                <Users className="h-4 w-4" />
                <span>Personal Information</span>
              </h3>
              <ul>
                <li>Name, email address, and company information when you create an account;</li>
                <li>Payment information (processed by Stripe, not stored by us);</li>
                <li>Usage data, such as pages visited and time spent on the site;</li>
                <li>Communication data, such as emails and support tickets.</li>
              </ul>
              <h3 className="flex items-center space-x-2 mb-2">
                <Database className="h-4 w-4" />
                <span>Non-Personal Information</span>
              </h3>
              <ul>
                <li>Browser type, device information, and IP address;</li>
                <li>Cookies and similar tracking technologies (see Cookie Policy below).</li>
              </ul>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Zap className="h-5 w-5 text-purple-500" />
                <span>3. How We Use Your Information</span>
              </h2>
              <p>We use the information we collect for the following purposes:</p>
              <ul>
                <li>To provide, maintain, and improve the Service;</li>
                <li>To process payments and manage subscriptions;</li>
                <li>To communicate with you about your account and updates;</li>
                <li>To analyze usage and optimize the Service;</li>
                <li>To comply with legal obligations and protect our rights.</li>
              </ul>
              <p>We do not sell your personal data to third parties.</p>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Download className="h-5 w-5 text-indigo-500" />
                <span>4. Your Rights (GDPR Compliance)</span>
              </h2>
              <p>Under the UK GDPR and Data Protection Act 2018, you have the following rights:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of your personal data;</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data;</li>
                <li><strong>Erasure:</strong> Request deletion of your data (right to be forgotten);</li>
                <li><strong>Restriction:</strong> Limit processing of your data in certain cases;</li>
                <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format;</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests;</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent.</li>
              </ul>
              <p>To exercise these rights, contact us at support@wightwayconnect.uk. We respond within 30 days.</p>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Clock className="h-5 w-5 text-yellow-500" />
                <span>5. Data Security</span>
              </h2>
              <p>We implement appropriate technical and organizational measures to protect your data against unauthorized access, alteration, disclosure, or destruction. This includes encryption, access controls, and regular security audits.</p>
              <p>However, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.</p>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Mail className="h-5 w-5 text-blue-500" />
                <span>6. Cookies and Tracking</span>
              </h2>
              <p>We use cookies to enhance your experience, analyze usage, and maintain session state. Cookies are small files stored on your device.</p>
              <ul>
                <li><strong>Essential Cookies:</strong> Required for site functionality (e.g., authentication);</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how users interact with the Service;</li>
                <li><strong>Marketing Cookies:</strong> Used for targeted advertising (opt-out available).</li>
              </ul>
              <p>You can manage cookies through your browser settings. Disabling cookies may limit functionality.</p>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Building className="h-5 w-5 text-green-500" />
                <span>7. Data Retention</span>
              </h2>
              <p>We retain your personal data only as long as necessary for the purposes outlined in this Policy or as required by law. Account data is retained for the duration of your subscription plus 30 days post-cancellation.</p>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Globe className="h-5 w-5 text-purple-500" />
                <span>8. International Data Transfers</span>
              </h2>
              <p>Data is processed in the UK and EEA. For non-EEA users, we use Standard Contractual Clauses (SCCs) to ensure adequate protection during transfers.</p>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <span>9. Children's Privacy</span>
              </h2>
              <p>Our Service is not intended for children under 16. We do not knowingly collect data from children. If we learn we have, we will delete it promptly.</p>
            </section>

            <section>
              <h2 className="flex items-center space-x-2 mb-2">
                <Mail className="h-5 w-5 text-blue-500" />
                <span>10. Contact Us</span>
              </h2>
              <p>If you have questions about this Privacy Policy, please contact our team:</p>
              <ul>
                <li>Email: support@wightwayconnect.uk</li>
                <li>Address: LPIWuk LTD, Building 41, Newport Road, Cowes,  United Kingdom, PO31 8BX</li>
              </ul>
              <p className="text-sm text-muted-foreground">Last updated: October 2025</p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Privacy;